import { Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import Services from "./services/services";
import CostEstimateSummary from "./summary/costestimatesummary";
import Supplies from "./supplies/supplies";
import OneTimeCosts from "./onetimecosts/onetimecosts";
import useApi from "../../../../hooks/use-api";
import MultiViewAddEdit from "./multiviewaddedit";
import { useOutletContext } from "react-router-dom";
import PermissionReadOnlyBoundary from "../../../../auth/PermissionReadOnlyBoundary";


const CostEstimate = () => {
    const [isLoading,setIsLoading] = useState(true);
    const [addEditIndex, setAddEditIndex] = useState(null);
    const [editItem, setEditItem] = useState(null);
    const [addEditMode, setAddEditMode] = useState(null);
    const [supplyTypes,setSupplyTypes] = useState([]);
    const [serviceTypes,setServiceTypes] = useState([]);
    const [oneTimeCostTypes,setOneTimeCostTypes] = useState([]);
    const [factors,setFactors] = useState([]);
    const [serviceCosts,setServiceCosts] = useState([]);
    const [supplyCosts,setSupplyCosts] = useState([]);
    const [oneTimeCosts,setOneTimeCosts] = useState([]);

    const {ApplicationID: applicationId, pagePermissions = {}, currentPageReadOnly = false} = useOutletContext();
    const canAddEntries = pagePermissions.canAdd === true;
    const canUpdateEntries = pagePermissions.canUpdate === true;
    const canDeleteEntries = pagePermissions.canDelete === true;

    const toggleAddEdit = (item, index, mode = item === null ? 'add' : 'edit') => {
        if(item === null && !canAddEntries){
            return;
        }

        const nextMode = item !== null && !canUpdateEntries ? 'view' : mode;

        setEditItem(item);
        setAddEditIndex(index);
        setAddEditMode(nextMode);
    };

    const returnFunction = () => {
        setEditItem(null);
        setAddEditIndex(null);
        setAddEditMode(null);
    };

    const {getRequest} = useApi();

    const getAll = async() => {
        const [definitionData,costEstimateData] = await Promise.all([
            getRequest('CostEstimate_Definitions'),
            getRequest('CostEstimate',{ApplicationID: applicationId,timestamp: Date.now()})
        ]);

        setIsLoading(false);

        const [extractedDefinitionData,definitionStatus] = definitionData;
        const [extractedCEData,ceStatus] = costEstimateData;

        if(definitionStatus === 200 && ceStatus === 200){
            setSupplyTypes(extractedDefinitionData.supplytypes);
            setServiceTypes(extractedDefinitionData.servicetypes);
            setOneTimeCostTypes(extractedDefinitionData.onetimecosttypes);
            setFactors(extractedDefinitionData.factors);

            setServiceCosts(extractedCEData.servicecosts);
            setSupplyCosts(extractedCEData.supplycosts);
            setOneTimeCosts(extractedCEData.onetimecosts);
        }
    }

    const refresh = async() => {
        const [data,status] = await getRequest('CostEstimate',{ApplicationID: applicationId, timestamp: Date.now()});

        if(status === 200){
            setServiceCosts(data.servicecosts);
            setSupplyCosts(data.supplycosts);
            setOneTimeCosts(data.onetimecosts);
        }
    }

    useEffect(() => {
        getAll();
    },[])

    if(isLoading){
        return(
            <div>loading...</div>
        )
    }

    const addEditReadOnly = currentPageReadOnly
        || addEditMode === 'view'
        || (editItem !== null && !canUpdateEntries)
        || (editItem === null && addEditIndex !== null && !canAddEntries);

    const renderReadOnlyAddEdit = (children) => (
        <PermissionReadOnlyBoundary readOnly={addEditReadOnly}>
            {children}
        </PermissionReadOnlyBoundary>
    );

    const _RenderAddEditIndex = (index) => {



        switch (index) {
            case 1:
                return (
                    <Col xs={12}>
                        {renderReadOnlyAddEdit(
                            <MultiViewAddEdit
                                ApplicationID={applicationId}
                                title='Service'
                                expenseTypeOptions={serviceTypes}
                                frequencyOptions={factors}
                                targetId={editItem}
                                getEnpoint='CostEstimate_Service'
                                addEndpoint='CostEstimate_Service_Add'
                                updateEndpoint='CostEstimate_Service_Update'
                                returnFunction={returnFunction}
                                refreshFunction={refresh}
                                readOnly={addEditReadOnly}
                            />
                        )}
                    </Col>
                )
            case 2:
                return (
                    <Col xs={12}>
                        {renderReadOnlyAddEdit(
                            <MultiViewAddEdit
                                ApplicationID={applicationId}
                                title="Supply"
                                expenseTypeOptions={supplyTypes}
                                frequencyOptions={factors}
                                targetId={editItem}
                                getEnpoint='CostEstimate_Supply'
                                addEndpoint='CostEstimate_Supply_Add'
                                updateEndpoint='CostEstimate_Supply_Update'
                                returnFunction={returnFunction}
                                refreshFunction={refresh}
                                readOnly={addEditReadOnly}
                            />
                        )}
                    </Col>
                )
            case 3:
                return (
                    <Col xs={12}>
                        {renderReadOnlyAddEdit(
                            <MultiViewAddEdit
                                ApplicationID={applicationId}
                                title='One Time'
                                expenseTypeOptions={oneTimeCostTypes}
                                frequencyOptions={factors}
                                targetId={editItem}
                                getEnpoint='CostEstimate_OneTime'
                                addEndpoint='CostEstimate_OneTime_Add'
                                updateEndpoint='CostEstimate_OneTime_Update'
                                restrictOneTime={true}
                                returnFunction={returnFunction}
                                refreshFunction={refresh}
                                readOnly={addEditReadOnly}
                            />
                        )}
                    </Col>
                )
            default:
                return (
                    <>
                        <Col xs={12} className="mb-2">
                            <CostEstimateSummary
                                services={serviceCosts}
                                supplies={supplyCosts}
                                oneTimeCosts={oneTimeCosts}
                            />
                        </Col>
                        <Col xs={12} md={4} className="mb-2">
                            <Services
                                toggleAddEditMode={toggleAddEdit}
                                addEditIndex={1}
                                serviceEntries={serviceCosts}
                                expenditureTypes={serviceTypes}
                                refreshFunction={refresh}
                                canAdd={canAddEntries}
                                canUpdate={canUpdateEntries}
                                canDelete={canDeleteEntries}
                            />
                        </Col>
                        <Col xs={12} md={4} className="mb-2">
                            <Supplies
                                toggleAddEditMode={toggleAddEdit}
                                addEditIndex={2}
                                supplyEntries={supplyCosts}
                                expenditureTypes={supplyTypes}
                                refreshFunction={refresh}
                                canAdd={canAddEntries}
                                canUpdate={canUpdateEntries}
                                canDelete={canDeleteEntries}
                            />
                        </Col>
                        <Col xs={12} md={4} className="mb-2">
                            <OneTimeCosts
                                toggleAddEditMode={toggleAddEdit}
                                addEditIndex={3}
                                oneTimeCostEntries={oneTimeCosts}
                                expenditureTypes={oneTimeCostTypes}
                                refreshFunction={refresh}
                                canAdd={canAddEntries}
                                canUpdate={canUpdateEntries}
                                canDelete={canDeleteEntries}
                            />
                        </Col>
                    </>
                )
        }
    }

    return (
        <Row>
            {
                _RenderAddEditIndex(addEditIndex)
            }
        </Row>
    );
}

export default CostEstimate;
